/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, select, takeLatest, call, race } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { StoreState } from 'store/store';
import {
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  AssetMediaType,
  FileFormatToFileTypeMap,
  Asset,
  AssetsApi,
  SearchUtils,
  ParentalControlSettings,
  HideUnsubscribed,
  LEGACY,
  Ducks,
  StorageKey,
  LocalStorage,
  MenuUiArea,
  MenuItem,
  MenuItemType,
} from '@wit/vtv-x-foundation-lib';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import RailBadgeEngine from 'core/services/core/badgeEngine/RailBadgeEngine';
import translate from 'services/i18n/translate';
import Constants from 'configs/Constants';
import * as Gdpr from '../../../core/store/ducks/gdpr';
import AppConfig from '../../../services/AppConfig';
import { filterInvalidEpgPrograms } from '../epg';
import { ESearchFilter } from '../../../types/components/Search';

export interface SearchRailUi {
  key: string;
  title: string;
  assets: Asset[];
}

export enum ActionTypes {
  SEARCH_SET_QUERY = 'app/search/SET_QUERY',
  SEARCH_QUERY_SUCCESS = 'app/search/SEARCH_QUERY_SUCCESS',
  SEARCH_REQUEST = 'app/search/REQUEST',
  SEARCH_SUCCESS = 'app/search/SUCCESS',
  SEARCH_FAIL = 'app/search/FAIL',
  SEARCH_NO_RESULTS = 'app/search/NO_RESULTS',
  SEARCH_SET_FILTER_REQUEST = 'app/search/SEARCH_SET_FILTER_REQUEST',
  SEARCH_LOADMORE_ASSETS = 'app/search/SEARCH_LOADMORE_ASSETS',
  SEARCH_RESET = 'app/search/SEARCH_RESET',
  SEARCH_ADD_RECENT_REQUEST = 'app/search/SEARCH_ADD_RECENT_REQUEST',
  SEARCH_ADD_RECENT_FOR_ALL_USERS_SUCCESS = 'app/search/SEARCH_ADD_RECENT_FOR_ALL_USERS_SUCCESS',
  SEARCH_LOAD_RECENT_FOR_USER = 'app/search/SEARCH_LOAD_RECENT_FOR_USER',
  SEARCH_LOAD_RECENT_FOR_USER_SUCCESS = 'app/search/SEARCH_LOAD_RECENT_FOR_USER_SUCCESS',
  CLEAR_SEARCH_ERROR = 'app/search/CLEAR_SEARCH_ERROR',
}

const BADGES_PRIORITY = BADGES_PRIORITY_PER_SECTION_RAIL.SEARCH.ALL_RESULTS_RAIL;

export enum SearchContext {
  RAIL = 'searchRail',
  GRID = 'searchGrid',
}

export interface Meta {
  pageSize: number;
}

export interface MetaSetQuery {
  query: string;
}

export interface ActiveFilter {
  filterKey: string;
  menuItemId?: number;
}

export interface SetFilter {
  activeFilter: ActiveFilter;
}

export interface SetFilterMeta {
  activeFilter: ActiveFilter;
  isRecomendation?: boolean;
  hasConsent?: boolean;
}
export interface ILoadMoreAssets {
  searchResultsPageIndex: number;
}

interface Payload {
  assets: Asset[];
  searchResultsPageIndex: number;
  noMoreResults?: boolean;
  loadMoreAssetsLoading?: boolean;
}
export interface IAssetsToUser {
  [key: string]: { assets: Asset[] };
}
export interface IUserRecent {
  userId: string;
  recent: IAssetsToUser;
}

type SearchReset = ActionRequest<ActionTypes.SEARCH_RESET>;
type SearchSetQuery = ActionRequest<ActionTypes.SEARCH_SET_QUERY, MetaSetQuery>;
type SearchQuerySuccess = ActionSuccess<ActionTypes.SEARCH_QUERY_SUCCESS, MetaSetQuery>;
type SearchRequest = ActionRequest<ActionTypes.SEARCH_REQUEST, Meta>;
type SearchSuccess = ActionSuccess<ActionTypes.SEARCH_SUCCESS, Payload>;
type SearchFail = ActionFail<ActionTypes.SEARCH_FAIL>;
type SearchNoResults = ActionRequest<ActionTypes.SEARCH_NO_RESULTS>;
type SearchFilterRequest = ActionRequest<ActionTypes.SEARCH_SET_FILTER_REQUEST, SetFilterMeta>;
type SearchLoadMoreAssets = ActionRequest<ActionTypes.SEARCH_LOADMORE_ASSETS, ILoadMoreAssets>;
type SearchAddRecentToUserRequest = ActionRequest<ActionTypes.SEARCH_ADD_RECENT_REQUEST, IUserRecent>;
type SearchAddRecentForAllUsersRequest = ActionSuccess<
  ActionTypes.SEARCH_ADD_RECENT_FOR_ALL_USERS_SUCCESS,
  IAssetsToUser
>;
type SearchLoadRecentForUser = ActionRequest<ActionTypes.SEARCH_LOAD_RECENT_FOR_USER>;
type SearchLoadRecentForUserSuccess = ActionSuccess<ActionTypes.SEARCH_LOAD_RECENT_FOR_USER_SUCCESS, Asset[]>;
type ClearSearchError = ActionSuccess<ActionTypes.CLEAR_SEARCH_ERROR>;

type Actions =
  | SearchReset
  | SearchRequest
  | SearchSuccess
  | SearchFail
  | SearchSetQuery
  | SearchQuerySuccess
  | SearchFilterRequest
  | SearchLoadMoreAssets
  | SearchNoResults
  | SearchAddRecentToUserRequest
  | SearchAddRecentForAllUsersRequest
  | SearchLoadRecentForUser
  | SearchLoadRecentForUserSuccess
  | ClearSearchError;

const Actions = {
  searchReset: (): SearchReset => ({ type: ActionTypes.SEARCH_RESET }),
  setQuery: (query: string): SearchSetQuery => ({
    type: ActionTypes.SEARCH_SET_QUERY,
    meta: { query },
  }),
  setQuerySuccess: (query: string): SearchQuerySuccess => ({
    type: ActionTypes.SEARCH_QUERY_SUCCESS,
    payload: { query },
  }),
  searchRequest: (pageSize: number): SearchRequest => ({
    type: ActionTypes.SEARCH_REQUEST,
    meta: { pageSize },
  }),
  success: (
    assets: Asset[],
    searchResultsPageIndex: number,
    noMoreResults?: boolean,
    loadMoreAssetsLoading?: boolean
  ): SearchSuccess => ({
    type: ActionTypes.SEARCH_SUCCESS,
    payload: { assets, searchResultsPageIndex, noMoreResults, loadMoreAssetsLoading },
  }),
  setSearchFail: (error: XError): SearchFail => ({
    type: ActionTypes.SEARCH_FAIL,
    error,
  }),
  setNoResults: (): SearchNoResults => ({
    type: ActionTypes.SEARCH_NO_RESULTS,
  }),
  setActiveFilterRequest: (
    filter: ActiveFilter,
    isRecomendation: boolean,
    hasConsent: boolean
  ): SearchFilterRequest => ({
    type: ActionTypes.SEARCH_SET_FILTER_REQUEST,
    meta: { activeFilter: filter, isRecomendation, hasConsent },
  }),
  loadMoreAssets: (searchResultsPageIndex: number): SearchLoadMoreAssets => ({
    type: ActionTypes.SEARCH_LOADMORE_ASSETS,
    meta: { searchResultsPageIndex },
  }),
  setRecentForUserRequest: (userId: string, recent: IAssetsToUser): SearchAddRecentToUserRequest => ({
    type: ActionTypes.SEARCH_ADD_RECENT_REQUEST,
    meta: { userId, recent },
  }),
  addRecentForAllUsersRequest: (recent: IAssetsToUser): SearchAddRecentForAllUsersRequest => ({
    type: ActionTypes.SEARCH_ADD_RECENT_FOR_ALL_USERS_SUCCESS,
    payload: recent,
  }),
  loadRecentAssetsForUser: (): SearchLoadRecentForUser => ({
    type: ActionTypes.SEARCH_LOAD_RECENT_FOR_USER,
  }),
  loadRecentAssetsForUserSuccess: (assets: Asset[]): SearchLoadRecentForUserSuccess => ({
    type: ActionTypes.SEARCH_LOAD_RECENT_FOR_USER_SUCCESS,
    payload: assets,
  }),
  clearSearchError: (): ClearSearchError => ({
    type: ActionTypes.CLEAR_SEARCH_ERROR,
  }),
};

export const ActionCreators = {
  searchReset: Actions.searchReset,
  setQuery: Actions.setQuery,
  searchAssets: Actions.searchRequest,
  setActiveFilter: Actions.setActiveFilterRequest,
  loadMoreAssets: Actions.loadMoreAssets,
  addToRecent: Actions.setRecentForUserRequest,
  initRecentAssets: Actions.loadRecentAssetsForUser,
  clearSeachError: Actions.clearSearchError,
};

const getNamespace = (searchQuery: string): string => {
  return `search_assets_${searchQuery}`;
};

function setLocalStorage(items: IAssetsToUser[] | IAssetsToUser) {
  LocalStorage.storeObject(StorageKey.RECENT_SEARCHES, items);
}

const FromLocalStoragePromise = {
  getRecentFromLocalStorage: async () => {
    const items = await LocalStorage.getObject(StorageKey.RECENT_SEARCHES);
    return items || null;
  },
};

async function getUserRecentFromLocalStorage(userId: string) {
  const getLocalStorage = await FromLocalStoragePromise.getRecentFromLocalStorage();
  const assetsTemp = (getLocalStorage as IAssetsToUser) || [];
  return assetsTemp[userId] ? assetsTemp[userId].assets : [];
}

async function getAllUsersRecentFromLocalStorage() {
  const getLocalStorage = await FromLocalStoragePromise.getRecentFromLocalStorage();
  const allAssets = getLocalStorage as IAssetsToUser[];
  return allAssets;
}

function getSearchRailsInitialState(): SearchRailUi[] {
  const searchRail: SearchRailUi = {
    key: 'Search_Results',
    title: translate['search.searchresults'],
    assets: [],
  };

  const rails: SearchRailUi[] = [searchRail];

  return rails;
}

// SELECTORS
export const Selectors = {
  getRails: (state: StoreState): SearchRailUi[] => state.search.searchRails,
  getResultAssets: (state: StoreState): Asset[] => state.search.searchRails[0]?.assets,
  getNoResults: (state: StoreState): boolean => state.search.noResults,
  getQuery: (state: StoreState): string => state.search.query,
  getSearchResultsPageIndex: (state: StoreState): number => state.search.searchResultsPageIndex,
  getActiveFilter: (state: StoreState): ActiveFilter => state.search.activeFilter,
  isSearchRequestComplete: (state: StoreState): boolean => state.search.isLoadingRequest,
  noMoreResults: (state: StoreState): boolean => state.search.noMoreResults,
  isSearchMoreAssetsLoading: (state: StoreState): boolean => state.search.isLoadingMoreAssets,
  completed: (state: StoreState): boolean => {
    const namespace = getNamespace(state.search.query);
    const isCompletePagination = Ducks.Pagination.Selectors.isCompleted(state, namespace);
    return isCompletePagination;
  },
  recentAssetsForUser: (state: StoreState): Asset[] => state.search.recentAssetsForUser,
  getError: (state: StoreState): XError | undefined => state.search.error,
};

// SEARCH FILTERS MEDIA TYPES:
// ALL -> EPG, MOVIE, SERIES, FOLDER
// On TV -> EPG
// On Demand -> MOVIE, SERIES, FOLDER

// Sagas
function* getSearchAssets(
  namespace: string,
  ksql: string,
  mediaTypes: AssetMediaType[],
  pageIndex: number,
  pageSize: number
) {
  const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
  const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

  const result: Asset[] = yield Ducks.Pagination.callPaginatedApi(
    namespace,
    pageIndex,
    pageSize,
    pageSize,
    AssetsApi.assetSearch,
    ksql,
    filesFormat,
    parentalControlSettings,
    mediaTypes,
    LEGACY.KalturaAssetOrderBy.RELEVANCY_DESC,
    HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
  );

  return result;
}

function* getRecomendationsAssets(menuItem: MenuItem, index: number, text: string): SagaIterator<Asset[]> {
  const resultData = yield select(Ducks.Recommendations.Selectors.assets, {
    menuUiArea: MenuUiArea.SEARCH,
    menuItemId: menuItem?.children[index].id,
    query: text,
  });
  return resultData;
}

function* searchAssets(): SagaIterator {
  const searchQuery: string = yield select(Selectors.getQuery);
  const searchFilter: ActiveFilter = yield select(Selectors.getActiveFilter);
  const menuItem: MenuItem | undefined = yield select(Ducks.Menu.Selectors.getMainMenuItem, MenuUiArea.SEARCH);
  const isRecomendations = yield select(Ducks.Recommendations.Selectors.isFeatureEnabled);
  const isGdprConsent = yield select(Gdpr.Selectors.hasAgreedToConsent);
  const searchResultsPageIndex: number = yield select(Selectors.getSearchResultsPageIndex);
  let assetsStored: Asset[] = [];
  let result: Asset[] = [];

  // Recomendations FLOW
  if (isRecomendations && isGdprConsent) {
    // Index value that determines witch Filter is used on search request on Recommendations context
    const indexRecomSearch = Number(searchFilter.filterKey) || 0;

    if (!menuItem) {
      return;
    }

    if (menuItem.children[indexRecomSearch].type === MenuItemType.EXTERNAL_SEARCH) {
      // Base Obj to context Search in Recommendations
      const recommContext: Ducks.Recommendations.RecommContext = {
        menuUiArea: MenuUiArea.SEARCH,
        menuItemId: menuItem.children[indexRecomSearch].id,
        query: searchQuery,
      };

      try {
        // Set Results
        yield put(Ducks.Recommendations.Creators.loadAssets(recommContext, false));

        const { requestSuccess, requestFail } = yield race({
          requestSuccess: Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.SUCCESS, recommContext),
          requestFail: Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.FAIL, recommContext),
        });

        if (requestSuccess) {
          // Get Results
          result = yield call(getRecomendationsAssets, menuItem, indexRecomSearch, searchQuery);
          // Filter invalid assets
          result = yield call(filterInvalidEpgPrograms, result);

          // Request with success but with no Results found
          if (result.length === 0) {
            yield put(Actions.setNoResults());
            return;
          }

          if (searchResultsPageIndex === 0) {
            // Has less results that Pagesize
            if (result.length <= Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS) {
              const resultWithBadges: Asset[] = yield call(
                RailBadgeEngine.getAssetsWithBadgesListFromRail,
                result,
                BADGES_PRIORITY
              );
              // Save them to the Store
              yield put(Actions.success(resultWithBadges, searchResultsPageIndex, true, false));
            }
            // Has more results than Pagesize
            if (result.length > Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS) {
              const nextPageAssets = result.slice(searchResultsPageIndex, Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS);

              const resultWithBadges: Asset[] = yield call(
                RailBadgeEngine.getAssetsWithBadgesListFromRail,
                nextPageAssets,
                BADGES_PRIORITY
              );
              // Save them to the Store
              yield put(Actions.success(resultWithBadges, searchResultsPageIndex + 1, false, true));
            }
          }

          if (searchResultsPageIndex > 0) {
            // Calculate the next indexes to grab the assets form the original array
            const startindex = searchResultsPageIndex * Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS;
            const endIndex = (searchResultsPageIndex + 1) * Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS;

            // Get the assets already stored from previous page
            assetsStored = yield select(Selectors.getResultAssets) || [];

            // Get the subset of records for this page
            const nextPageAssets = result.slice(startindex, endIndex);

            // Merge the already stored assets with the next page assets
            const assetsMerged = assetsStored !== undefined ? [...assetsStored, ...nextPageAssets] : nextPageAssets;

            const resultWithBadges: Asset[] = yield call(
              RailBadgeEngine.getAssetsWithBadgesListFromRail,
              assetsMerged,
              BADGES_PRIORITY
            );

            // It still has another page
            if (result.length > endIndex) {
              yield put(Actions.success(resultWithBadges, searchResultsPageIndex + 1, false, true));
            } else {
              yield put(Actions.success(resultWithBadges, searchResultsPageIndex, true, false));
            }
          }
        } else if (requestFail) {
          yield put(Actions.setSearchFail(XError.fromError(requestFail.error)));
        }
      } catch (error) {
        yield put(Actions.setSearchFail(XError.fromError(error)));
      }
    }
  } else if ((isRecomendations && !isGdprConsent) || !isRecomendations) {
    const namespace = getNamespace(searchQuery);
    let mediaTypes: AssetMediaType[] = [];
    switch (searchFilter.filterKey) {
      case ESearchFilter.ALL:
        // All
        mediaTypes = [AssetMediaType.MOVIE, AssetMediaType.SERIES, AssetMediaType.FOLDER, AssetMediaType.EPG];
        break;
      case ESearchFilter.ON_TV:
        // On TV
        mediaTypes = [AssetMediaType.EPG];
        break;
      case ESearchFilter.ON_DEMAND:
        // On Demand
        mediaTypes = [AssetMediaType.MOVIE, AssetMediaType.SERIES, AssetMediaType.FOLDER];
        break;
      case ESearchFilter.OPT_IN:
        return;
      default:
        yield put(Actions.setActiveFilterRequest({ filterKey: ESearchFilter.ALL }, isRecomendations, isGdprConsent));
        break;
    }
    const ksql = SearchUtils.createSearchFilter(searchQuery);
    if (!ksql) {
      yield put(Actions.setSearchFail(XError.fromError(new Error('Error: Invalid Ksql.'))));
      return;
    }
    try {
      // Get the previous stored assets on Store
      assetsStored = yield select(Selectors.getResultAssets) || [];

      // First page of the Results
      if (searchResultsPageIndex === 0) {
        // Clear all results in Pagination Store
        yield Ducks.Pagination.clearPaginatedApi(namespace);

        // Get Assets
        result = yield call(
          getSearchAssets,
          namespace,
          ksql,
          mediaTypes,
          searchResultsPageIndex,
          Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS
        );
        result = yield call(filterInvalidEpgPrograms, result);

        const resultWithBadges: Asset[] = yield call(
          RailBadgeEngine.getAssetsWithBadgesListFromRail,
          result,
          BADGES_PRIORITY
        );

        // Update Store
        if (result.length > 0) {
          yield put(Actions.success(resultWithBadges, searchResultsPageIndex + 1));
        } else {
          yield put(Actions.setNoResults());
          return;
        }
      }

      // Has more than one page of Results
      if (searchResultsPageIndex > 0) {
        // Call for the next page assets
        const resultNextPage = yield call(
          getSearchAssets,
          namespace,
          ksql,
          mediaTypes,
          searchResultsPageIndex,
          Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS
        );

        // Merge the already stored assets with the next page assets
        result = assetsStored !== undefined ? [...assetsStored, ...resultNextPage] : resultNextPage;
        result = yield call(filterInvalidEpgPrograms, result);

        const resultWithBadges: Asset[] = yield call(
          RailBadgeEngine.getAssetsWithBadgesListFromRail,
          result,
          BADGES_PRIORITY
        );

        // Update store with all Assets

        if (resultNextPage.length > 0) {
          // Set loading on next page request
          yield put(Actions.success(resultWithBadges, searchResultsPageIndex + 1, false, false));
        } else {
          yield put(Actions.success(resultWithBadges, searchResultsPageIndex, true, false));
        }
      }

      // Request with success but with no Results found
      if (result.length === 0) {
        yield put(Actions.setNoResults());
        return;
      }
    } catch (error) {
      yield put(Actions.setSearchFail(XError.fromError(error)));
    }
  }
}

function* setQuery(action: SearchSetQuery): SagaIterator {
  const { query } = action.meta;

  const previousSearchQuery: string = yield select(Selectors.getQuery);
  if (previousSearchQuery.length) {
    const namespace = getNamespace(previousSearchQuery);

    yield Ducks.Pagination.clearPaginatedApi(namespace);
  }

  yield put(Actions.setQuerySuccess(query));
}

function* setUserRecentSearch(action: SearchAddRecentToUserRequest): SagaIterator {
  const { userId, recent } = action.meta;
  const previousAssets: Asset[] = yield call(getUserRecentFromLocalStorage, userId);

  const mergeAssets = (): Asset[] => {
    if (previousAssets.length) {
      // Merge Two array
      const merged = [...recent[userId].assets, ...previousAssets];
      // Remove Duplicates
      const result = merged.reduce((arrayUniq, originalArray) => {
        if (!arrayUniq.some((obj: Asset) => obj.id === originalArray.id)) {
          arrayUniq.push(originalArray);
        }
        return arrayUniq;
      }, [] as Asset[]);

      return result;
    }
    return recent[userId].assets;
  };

  // Merge the two arrays
  const merged: IAssetsToUser = { [userId]: { assets: mergeAssets() } };
  yield put(Actions.addRecentForAllUsersRequest(merged));
}

function* setAllUsersRecentSearchSuccess(action: SearchAddRecentForAllUsersRequest): SagaIterator {
  try {
    // Get Assets from Redux Store
    const recent: IAssetsToUser = action.payload;
    // Get Assets from LocalStorage
    const allAssetsToUser: IAssetsToUser[] = yield call(getAllUsersRecentFromLocalStorage);

    // Has stored Assets in LocalStorage
    let finalObj = {};
    if (allAssetsToUser && recent) {
      finalObj = { ...allAssetsToUser, ...recent };

      // Save All Recent Assets for all users in LocalStorage
      setLocalStorage(finalObj);
    } else {
      // Dont have assets in LocalStorage, save the clicked one
      setLocalStorage(recent);
    }
  } catch (error) {
    yield put(Actions.setSearchFail(XError.fromError(error)));
  }
}

function* initAssetsFromLocalStorage(): SagaIterator {
  try {
    const userId = yield call(LocalStorage.getPreferredUserId);
    const assets = yield call(getUserRecentFromLocalStorage, userId);
    const assetsWithBadges: Asset[] = yield call(
      RailBadgeEngine.getAssetsWithBadgesListFromRail,
      assets,
      BADGES_PRIORITY
    );

    yield put(Actions.loadRecentAssetsForUserSuccess(assetsWithBadges));
  } catch (error) {
    yield put(Actions.setSearchFail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([
    takeLatest(ActionTypes.SEARCH_REQUEST, searchAssets),
    takeLatest(ActionTypes.SEARCH_SET_QUERY, setQuery),
    takeLatest(ActionTypes.SEARCH_LOADMORE_ASSETS, searchAssets),
    takeLatest(ActionTypes.SEARCH_ADD_RECENT_REQUEST, setUserRecentSearch),
    takeLatest(ActionTypes.SEARCH_ADD_RECENT_FOR_ALL_USERS_SUCCESS, setAllUsersRecentSearchSuccess),
    takeLatest(ActionTypes.SEARCH_LOAD_RECENT_FOR_USER, initAssetsFromLocalStorage),
  ]);
}

// REDUCERS
export interface ReducerState {
  searchRails: SearchRailUi[];
  error: XError | undefined;
  noResults: boolean;
  query: string;
  searchResultsPageIndex: number;
  activeFilter: ActiveFilter;
  isLoadingMoreAssets: boolean;
  noMoreResults: boolean;
  isLoadingRequest: boolean;
  recentSearches: IAssetsToUser;
  recentAssetsForUser: Asset[];
}

const initialState: ReducerState = {
  searchRails: getSearchRailsInitialState(),
  error: undefined,
  noResults: false,
  query: '',
  searchResultsPageIndex: 0,
  activeFilter: {
    filterKey: '',
  },
  isLoadingMoreAssets: false,
  noMoreResults: false,
  isLoadingRequest: false,
  recentSearches: {},
  recentAssetsForUser: [],
};

export default function search(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.SEARCH_RESET:
      return initialState;
    case ActionTypes.SEARCH_SET_FILTER_REQUEST:
      return {
        ...state,
        activeFilter: action.meta.activeFilter,
        searchRails: [],
        searchResultsPageIndex: 0,
      };
    case ActionTypes.SEARCH_SET_QUERY:
      return {
        ...state,
        noResults: false,
        isLoadingRequest: true,
      };
    case ActionTypes.SEARCH_REQUEST:
      return {
        ...state,
        noResults: false,
        isLoadingRequest: true,
      };
    case ActionTypes.SEARCH_QUERY_SUCCESS:
      return {
        ...state,
        query: action.payload.query,
        searchResultsPageIndex: 0,
      };
    case ActionTypes.SEARCH_SUCCESS: {
      const { assets, searchResultsPageIndex, loadMoreAssetsLoading, noMoreResults } = action.payload;
      return {
        ...state,
        searchRails: [
          {
            ...state.searchRails[0],
            assets,
          },
        ],
        searchResultsPageIndex,
        noResults: false,
        isLoadingRequest: false,
        isLoadingMoreAssets: loadMoreAssetsLoading !== undefined ? loadMoreAssetsLoading : state.isLoadingMoreAssets,
        noMoreResults: noMoreResults !== undefined ? noMoreResults : state.noMoreResults,
      };
    }
    case ActionTypes.SEARCH_FAIL:
      return {
        ...state,
        noResults: true,
        isLoadingRequest: false,
        isLoadingMoreAssets: false,
        noMoreResults: false,
        error: action.error,
      };
    case ActionTypes.SEARCH_LOADMORE_ASSETS:
      return {
        ...state,
        isLoadingMoreAssets: true,
        searchResultsPageIndex: action.meta.searchResultsPageIndex,
      };
    case ActionTypes.SEARCH_NO_RESULTS:
      return {
        ...state,
        noResults: true,
        searchRails: [
          {
            ...state.searchRails[0],
            assets: [],
          },
        ],
        isLoadingMoreAssets: false,
        isLoadingRequest: false,
      };
    case ActionTypes.SEARCH_ADD_RECENT_FOR_ALL_USERS_SUCCESS:
      return {
        ...state,
        recentSearches: action.payload,
      };
    case ActionTypes.SEARCH_LOAD_RECENT_FOR_USER_SUCCESS:
      return {
        ...state,
        recentAssetsForUser: action.payload,
      };
    case ActionTypes.CLEAR_SEARCH_ERROR:
      return {
        ...state,
        error: undefined,
      };
    default:
      return state;
  }
}
